/**
 * Edit Organization Modal
 *
 * 4-tab modal for managing existing organizations
 * Tabs:
 * - Details: Name, contact info, status
 * - Subscription: Tier, limits, duration
 * - Usage: View aggregate counts (GDPR-safe)
 * - GDPR: Audit logs, data export, deletion
 */

import React, { useState, useEffect } from 'react';
import {
  Building2,
  CreditCard,
  BarChart3,
  Shield,
  X,
  Save,
  AlertTriangle
} from 'lucide-react';

const TABS = [
  { id: 'details', label: 'Details', icon: Building2 },
  { id: 'subscription', label: 'Subscription', icon: CreditCard },
  { id: 'usage', label: 'Usage', icon: BarChart3 },
  { id: 'gdpr', label: 'GDPR', icon: Shield }
];

const SUBSCRIPTION_STATUSES = [
  { value: 'active', label: 'Active', color: 'text-green-600' },
  { value: 'trial', label: 'Trial', color: 'text-blue-600' },
  { value: 'suspended', label: 'Suspended', color: 'text-amber-600' },
  { value: 'cancelled', label: 'Cancelled', color: 'text-red-600' },
  { value: 'expired', label: 'Expired', color: 'text-slate-600' }
];

const SUBSCRIPTION_TIERS = {
  starter: { name: 'Starter', max_users: 5, max_vessels: 2 },
  standard: { name: 'Standard', max_users: 10, max_vessels: 5 },
  professional: { name: 'Professional', max_users: 25, max_vessels: 15 },
  enterprise: { name: 'Enterprise', max_users: 100, max_vessels: 50 }
};

export const EditOrganizationModal = ({
  isOpen,
  organization,
  onClose,
  onSave,
  onDelete,
  onReactivate
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);

  useEffect(() => {
    if (organization) {
      setFormData(organization);
      setHasChanges(false);
      setShowDeleteConfirm(false);
      setShowReactivateConfirm(false);
    }
  }, [organization]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setError(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      await onSave(organization.id, formData);
      setHasChanges(false);
      onClose();
    } catch (err) {
      console.error('Error saving organization:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await onDelete(organization.id);
      onClose();
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError(err.message || 'Failed to deactivate organisation');
      setShowDeleteConfirm(false);
    }
  };

  const handleConfirmReactivate = async () => {
    try {
      await onReactivate(organization.id);
      onClose();
    } catch (err) {
      console.error('Error reactivating organization:', err);
      setError(err.message || 'Failed to reactivate organisation');
      setShowReactivateConfirm(false);
    }
  };

  const calculateDaysUntilExpiry = () => {
    if (!formData.subscription_end_date) return null;
    const endDate = new Date(formData.subscription_end_date);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (!isOpen || !organization) return null;

  const daysUntilExpiry = calculateDaysUntilExpiry();
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry < 0;
  const currentUserCount = organization.memberCount || 0;
  const currentVesselCount = organization.vesselCount || 0;
  const userUsagePercent = formData.max_users ? Math.round((currentUserCount / formData.max_users) * 100) : 0;
  const vesselUsagePercent = formData.max_vessels ? Math.round((currentVesselCount / formData.max_vessels) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="h-6 w-6" />
            <div>
              <h2 className="text-xl font-bold">Edit Organization</h2>
              <p className="text-sm text-slate-300">{organization.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            disabled={isSaving}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 bg-slate-50 px-6">
          <div className="flex gap-1">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    px-4 py-3 flex items-center gap-2 border-b-2 transition-colors
                    ${activeTab === tab.id
                      ? 'border-cyan-600 text-cyan-600 bg-white'
                      : 'border-transparent text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                    }
                  `}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Tab: Details */}
          {activeTab === 'details' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Organization Details</h3>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Organization Name
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contact Email
                </label>
                <p className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-700 text-sm">
                  {formData.contact_email || <span className="text-slate-400">Not set</span>}
                </p>
                <p className="mt-1 text-sm text-slate-500">Managed separately — contact support to update</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contact Phone
                </label>
                <p className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-700 text-sm">
                  {formData.contact_phone || <span className="text-slate-400">Not set</span>}
                </p>
                <p className="mt-1 text-sm text-slate-500">Managed separately — contact support to update</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Status
                </label>
                <p className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-700 text-sm">
                  {SUBSCRIPTION_STATUSES.find(s => s.value === (formData.subscription_status || 'active'))?.label || formData.subscription_status || 'Active'}
                </p>
                <p className="mt-1 text-sm text-slate-500">Managed separately — contact support to update</p>
              </div>
            </div>
          )}

          {/* Tab: Subscription */}
          {activeTab === 'subscription' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Subscription Management</h3>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Subscription settings are managed in the billing system — changes made here will not be saved.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Current Tier
                </label>
                <p className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-700 text-sm">
                  {SUBSCRIPTION_TIERS[formData.tier]?.name || formData.tier || 'Standard'}
                </p>
              </div>

              <div className="border-t border-slate-200 pt-4 mt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Subscription Period</h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Start Date
                    </label>
                    <p className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-700 text-sm">
                      {formData.subscription_start_date ? formatDate(formData.subscription_start_date) : 'N/A'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      End Date
                    </label>
                    <p className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-700 text-sm">
                      {formData.subscription_end_date ? formatDate(formData.subscription_end_date) : 'N/A'}
                    </p>
                  </div>
                </div>

                {daysUntilExpiry !== null && (
                  <div className={`mt-2 p-3 rounded-lg ${isExpired ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                    <p className="text-sm font-medium">
                      {isExpired
                        ? `Expired ${Math.abs(daysUntilExpiry)} days ago`
                        : `Active (${daysUntilExpiry} days remaining)`
                      }
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200 pt-4 mt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Usage Limits</h4>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Maximum User Logins
                    </label>
                    <p className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-700 text-sm">
                      {formData.max_users || 10}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">Currently using: {currentUserCount} users</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Maximum Vessels
                    </label>
                    <p className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-700 text-sm">
                      {formData.max_vessels || 5}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">Currently using: {currentVesselCount} vessels</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab: Usage */}
          {activeTab === 'usage' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Usage Overview (Read-Only)</h3>

              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">👥 User Logins</span>
                    <span className="text-lg font-bold text-slate-800">
                      {currentUserCount} / {formData.max_users || 0}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-4 overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        userUsagePercent >= 90 ? 'bg-red-600' :
                        userUsagePercent >= 75 ? 'bg-amber-600' :
                        'bg-cyan-600'
                      }`}
                      style={{ width: `${Math.min(userUsagePercent, 100)}%` }}
                    />
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{userUsagePercent}% used</p>

                  <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-700">
                      • Breakdown by role hidden for GDPR compliance<br />
                      • Use Impersonation to view individual users
                    </p>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">🚢 Vessels</span>
                    <span className="text-lg font-bold text-slate-800">
                      {currentVesselCount} / {formData.max_vessels || 0}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-4 overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        vesselUsagePercent >= 90 ? 'bg-red-600' :
                        vesselUsagePercent >= 75 ? 'bg-amber-600' :
                        'bg-cyan-600'
                      }`}
                      style={{ width: `${Math.min(vesselUsagePercent, 100)}%` }}
                    />
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{vesselUsagePercent}% used</p>

                  <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-700">
                      • Vessel names hidden for GDPR compliance<br />
                      • Use Impersonation to view vessel details
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                <p className="text-sm text-cyan-800">
                  ℹ️ <strong>GDPR Compliance:</strong> Individual user/vessel names are not displayed here.
                  Only aggregate counts are shown. Use the Impersonation feature with proper audit trail
                  if you need to view specific details.
                </p>
              </div>
            </div>
          )}

          {/* Tab: GDPR */}
          {activeTab === 'gdpr' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">GDPR & Data Management</h3>

              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Data Retention</h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-slate-600">Organization created:</dt>
                      <dd className="font-medium">{formatDate(organization.createdAt)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-600">Last updated:</dt>
                      <dd className="font-medium">{formatDate(organization.updatedAt)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-600">Data retention:</dt>
                      <dd className="font-medium">7 years (UK maritime law)</dd>
                    </div>
                  </dl>
                </div>

                <div className="border-t border-red-200 pt-4 mt-6">
                  <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Danger Zone
                  </h4>

                  {organization.active === false ? (
                    /* Organisation is currently inactive — show Reactivate */
                    !showReactivateConfirm ? (
                      <button
                        type="button"
                        onClick={() => setShowReactivateConfirm(true)}
                        className="w-full px-4 py-3 bg-green-50 hover:bg-green-100 text-green-700 border border-green-300 rounded-lg transition-colors text-left font-medium"
                      >
                        Reactivate Organisation
                      </button>
                    ) : (
                      <div className="p-4 bg-green-50 border border-green-300 rounded-lg">
                        <p className="text-sm text-slate-800 mb-3">
                          Reactivate this organisation? Users will regain access immediately. All data is intact.
                        </p>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setShowReactivateConfirm(false)}
                            className="px-4 py-2 text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-lg transition-colors text-sm font-medium"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleConfirmReactivate}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
                          >
                            Yes, reactivate
                          </button>
                        </div>
                      </div>
                    )
                  ) : (
                    /* Organisation is currently active — show Deactivate */
                    !showDeleteConfirm ? (
                      <button
                        type="button"
                        onClick={handleDelete}
                        className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 border border-red-300 rounded-lg transition-colors text-left font-medium"
                      >
                        Deactivate Organisation
                      </button>
                    ) : (
                      <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg">
                        <p className="text-sm text-slate-800 mb-3">
                          <strong>Deactivate this organisation?</strong> This will prevent all users in the organisation from logging in. Their data is preserved and the organisation can be reactivated later by a super admin.
                        </p>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setShowDeleteConfirm(false)}
                            className="px-4 py-2 text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-lg transition-colors text-sm font-medium"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleConfirmDelete}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium"
                          >
                            Yes, deactivate
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-50 p-6 rounded-b-2xl border-t border-slate-200 flex items-center justify-between">
          <div>
            {hasChanges && (
              <p className="text-sm text-amber-600">
                ⚠️ You have unsaved changes
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
            >
              {hasChanges ? 'Cancel' : 'Close'}
            </button>

            {activeTab !== 'gdpr' && activeTab !== 'usage' && activeTab !== 'subscription' && (
              <button
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
                className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors flex items-center gap-2 font-medium disabled:bg-slate-400"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Changes
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditOrganizationModal;
